import { cn } from '@newfront-insurance/core-ui/v2';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { forwardRef } from 'react';

import { useSlashCommandListState } from './use-slash-command-list-state';
import {
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_ICONS,
  isSlashPromptCommand,
  type PromptCommand,
} from '../../../../lib/slash-command-constants';

export type SlashCommandListVariant = 'default' | 'compact';

interface SlashCommandListProps {
  commands: PromptCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: PromptCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  /**
   * Visual variant:
   * - 'default': Standard sizing for desktop/wide panels (popover)
   * - 'compact': Smaller sizing for narrow panels (e.g., Outlook add-in)
   */
  variant?: SlashCommandListVariant;
  className?: string;
  /**
   * Whether to show the keyboard hints footer
   */
  showKeyboardHints?: boolean;
}

// Variant-specific styles
const styles = {
  default: {
    header: 'bg-steel-100 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    categoryHeader: 'bg-steel-50 px-3.5 py-2.5',
    categoryTitle: 'text-steel-500 text-sm font-semibold',
    categoryCount: 'text-steel-400 text-sm',
    footer: 'bg-steel-50 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-sm',
    listMaxHeight: 'max-h-[400px]',
    // Item styles
    itemContainer: 'hover:bg-steel-50',
    itemContainerSelected: 'bg-steel-100',
    itemContainerExpanded: 'bg-brand-100',
    itemPadding: 'px-3.5 py-2.5 gap-3',
    iconContainer: 'size-9 bg-brand-100',
    iconContainerExpanded: 'border-steel-200 border',
    icon: 'size-[18px] text-brand-400',
    handleText: 'text-steel-500 text-sm font-semibold',
    descriptionText: 'text-steel-400 text-sm',
    chevronSize: 'size-4',
    chevronColor: 'text-steel-400 hover:text-steel-500',
    expandedContainer: 'mx-3.5 mb-2.5 bg-steel-50 border-steel-200',
    expandedLabel: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    expandedContent: 'border-steel-200 bg-white',
    expandedText: 'text-steel-400 text-sm leading-4',
    titleText: 'text-steel-500 text-sm font-semibold',
    subLabelText: 'text-steel-400 text-xs font-semibold tracking-[1.2px] uppercase',
    skillBadge: 'border-steel-200 bg-white text-steel-400 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
    exampleHandle: 'text-fire-400 font-mono',
  },
  compact: {
    header: 'bg-[#f4f5f6] px-3.5 py-2.5',
    headerText: 'text-[12px] font-semibold tracking-[1.2px] text-[#546a83] uppercase',
    categoryHeader: 'bg-[#f4f5f6] px-3.5 py-2.5',
    categoryTitle: 'text-[12px] font-semibold text-[#262d46]',
    categoryCount: 'text-[12px] text-[#546a83]',
    footer: 'bg-[#f4f5f6] px-3.5 py-2.5',
    footerText: 'text-[12px] text-[#546a83]',
    listMaxHeight: '', // No max height for compact - uses flex-1
    // Item styles
    itemContainer: 'bg-white border-b border-[#dee2e7]',
    itemContainerSelected: 'bg-[#f4f5f6]',
    itemContainerExpanded: 'bg-[#f4f8fe]',
    itemPadding: 'px-2.5 py-2.5 gap-2.5 items-start',
    iconContainer: 'size-9 bg-[#f4f8fe]',
    iconContainerExpanded: 'border border-[#bec7d0]',
    icon: 'size-[18px] text-[#0957c3]',
    handleText: 'text-[12px] font-semibold text-[#262d46]',
    descriptionText: 'text-[12px] leading-4 text-[#546a83]',
    chevronSize: 'size-3',
    chevronColor: 'text-[#546a83] hover:text-[#262d46]',
    expandedContainer: 'mx-2.5 mb-2.5 bg-[#fafbfc] border-[#dee2e7]',
    expandedLabel: 'text-[12px] font-semibold tracking-[1.2px] text-[#546a83] uppercase',
    expandedContent: 'border-[#dee2e7] bg-white',
    expandedText: 'text-[12px] leading-4 text-[#546a83]',
    titleText: 'text-[12px] font-semibold text-[#262d46]',
    subLabelText: 'text-[11px] font-semibold tracking-[1.2px] text-[#546a83] uppercase',
    skillBadge: 'rounded-full border border-[#dee2e7] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#546a83]',
    exampleHandle: 'text-[#ff7a7a] font-mono',
  },
} as const;

export function SlashCommandList({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
  variant = 'default',
  className,
  showKeyboardHints = true,
}: SlashCommandListProps) {
  const {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef,
  } = useSlashCommandListState({
    commands,
    searchQuery,
    isOpen,
    onSelect,
    onClose,
    inputRef,
  });

  if (!isOpen || filteredCommands.length === 0) {
    return null;
  }

  const s = styles[variant];
  let itemIndex = 0;

  return (
    <div className={cn('flex w-full flex-col overflow-hidden', className)}>
      {/* Header */}
      <div className={s.header}>
        <span className={s.headerText}>Commands & Skills</span>
      </div>

      {/* Command List */}
      <div ref={listRef} className={cn('overflow-y-auto', variant === 'default' ? s.listMaxHeight : 'flex-1')}>
        {groupedCommands.map((group) => (
          <div key={group.category}>
            {/* Category Header */}
            <div className={cn('flex items-center justify-between', s.categoryHeader)}>
              <span className={s.categoryTitle}>{CATEGORY_DISPLAY_NAMES[group.category] || group.category}</span>
              <span className={s.categoryCount}>
                {group.commands.length} {group.commands.length === 1 ? 'command' : 'commands'}
              </span>
            </div>

            {/* Commands in category */}
            {group.commands.map((command) => {
              const currentIndex = itemIndex;
              const isSelected = currentIndex === selectedIndex;
              const isExpanded = expandedCommandUuid === command.id;
              const Icon = CATEGORY_ICONS[command.category] || FileText;
              itemIndex += 1;

              return (
                <SlashCommandItem
                  key={command.id}
                  ref={(el) => registerItemRef(currentIndex, el)}
                  command={command}
                  icon={Icon}
                  isSelected={isSelected}
                  isExpanded={isExpanded}
                  onToggleExpand={() => toggleExpanded(command.id)}
                  onClick={() => onSelect(command)}
                  variant={variant}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer - Keyboard Hints */}
      {showKeyboardHints && (
        <div className={s.footer}>
          <span className={s.footerText}>
            <kbd className="rounded px-1 py-0.5">↑↓</kbd> to navigate{' '}
            <kbd className="ml-2 rounded px-1 py-0.5">Tab</kbd>/<kbd className="rounded px-1 py-0.5">Enter</kbd> to
            select <kbd className="ml-2 rounded px-1 py-0.5">Esc</kbd> to cancel
          </span>
        </div>
      )}
    </div>
  );
}

interface SlashCommandItemProps {
  command: PromptCommand;
  icon: React.ComponentType<{ className?: string }>;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
  variant: SlashCommandListVariant;
}

const SlashCommandItem = forwardRef<HTMLButtonElement, SlashCommandItemProps>(
  ({ command, icon: Icon, isSelected, isExpanded, onToggleExpand, onClick, variant }, ref) => {
    const s = styles[variant];
    const isSkill = command.kind === 'skill';
    const showExpandedToggle = Boolean(command.prompt || command.exampleUsage || isSkill);

    return (
      <div
        className={cn(
          'flex w-full flex-col transition-colors',
          s.itemContainer,
          isExpanded && s.itemContainerExpanded,
          isSelected && !isExpanded && s.itemContainerSelected,
        )}
      >
        {/* Command Header Row */}
        <button ref={ref} type="button" className={cn('flex w-full text-left', s.itemPadding)} onClick={onClick}>
          {/* Icon */}
          <div
            className={cn(
              'flex shrink-0 items-center justify-center rounded-sm',
              s.iconContainer,
              isExpanded && s.iconContainerExpanded,
            )}
          >
            <Icon className={s.icon} />
          </div>

          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {variant === 'compact' ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    {isSkill && command.title ? <span className={s.titleText}>{command.title}</span> : null}
                    <span className={s.handleText}>/{command.handle}</span>
                  </div>
                  {showExpandedToggle ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand();
                      }}
                      className={cn('shrink-0 p-1', s.chevronColor)}
                      aria-label={isExpanded ? 'Collapse command details' : 'Expand command details'}
                    >
                      {isExpanded ? <ChevronUp className={s.chevronSize} /> : <ChevronDown className={s.chevronSize} />}
                    </button>
                  ) : null}
                </div>
                <span className={s.descriptionText}>{command.description}</span>
              </>
            ) : (
              <>
                {isSkill && command.title ? (
                  <div className="flex items-center gap-2">
                    <span className={s.titleText}>{command.title}</span>
                  </div>
                ) : null}
                <span className={s.handleText}>/{command.handle}</span>
                <span className={cn(s.descriptionText, 'truncate')}>{command.description}</span>
              </>
            )}
          </div>

          {/* Expand/Collapse Chevron - separate for default variant */}
          {variant === 'default' && showExpandedToggle && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              className={cn('shrink-0 p-1', s.chevronColor)}
              aria-label={isExpanded ? 'Collapse command details' : 'Expand command details'}
            >
              {isExpanded ? <ChevronUp className={s.chevronSize} /> : <ChevronDown className={s.chevronSize} />}
            </button>
          )}
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className={cn('flex flex-col gap-2.5 rounded-[5px] border p-2', s.expandedContainer)}>
            {isSlashPromptCommand(command) && command.prompt ? (
              <>
                <div className={s.expandedLabel}>Full prompt</div>
                <div className={cn('rounded-[5px] border p-2', s.expandedContent)}>
                  <p className={cn(s.expandedText, 'whitespace-pre-wrap')}>{command.prompt}</p>
                </div>
              </>
            ) : null}

            {isSkill ? (
              <>
                <div className={s.expandedLabel}>Skill details</div>
                <div className={cn('rounded-[5px] border p-2', s.expandedContent)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      {command.title ? <div className={s.titleText}>{command.title}</div> : null}
                      <div className={s.subLabelText}>Use `/{command.handle}` to load this skill</div>
                    </div>
                  </div>
                  <p className={cn(s.expandedText, 'mt-2 whitespace-pre-wrap')}>{command.description}</p>
                </div>
              </>
            ) : null}

            {/* Example Usage Section */}
            {command.exampleUsage && (
              <>
                <div className={s.expandedLabel}>Example usage</div>
                <div className={cn('rounded-[5px] border p-2', s.expandedContent)}>
                  <p className={s.expandedText}>
                    <span className={s.exampleHandle}>/{command.handle}</span> <span>{command.exampleUsage}</span>
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

SlashCommandItem.displayName = 'SlashCommandItem';
